<template>
    <div class="col-span-12 md:hidden" v-if="shouldShowCard">
        <div
            v-if="card.special_offer"
            class="w-full flex items-start gap-2 self-stretch rounded-t-lg border relative z-0 mt-2 -mb-2"
            style="
                padding: 8px 24px 16px 16px;
                border-color: var(--10x-Orange-200, #fed1c7);
                background: var(--Special-Case-Orange-Bg-Light, #fef0e7);
            "
        >
            <Icon icon="flame" class="w-5 h-5 text-orange" />
            <span class="text-sm leading-5 font-medium text-orange">
                Special welcome offer
            </span>
        </div>
        <div
            class="w-full flex items-start gap-2 self-stretch rounded-t-lg relative z-10 -mb-2"
            style="
                padding: 8px 24px 16px 16px;
                background: var(--Primary-10x-Orange, #fd421d);
            "
        >
            <Icon icon="sparkles" class="w-5 h-5 text-white shrink-0" />
            <span
                class="text-sm leading-5 font-medium text-white whitespace-nowrap"
            >
                {{ user && user.name ? `${user.name}'s` : 'Your' }} top
                recommended card
            </span>
        </div>
        <div
            class="w-full rounded-lg bg-white dark:bg-slate-800 border border-orange overflow-hidden shadow-sm relative z-20"
        >
            <CardExclusion
                :card="card"
                :offenders="offenders[card.id]"
                v-if="card.hidden && !hideOverlayOnly && !showHidden"
                @hide-card="hideCard"
            />

            <div class="p-4">
                <div
                    class="flex flex-col items-center self-stretch pt-4 px-8 pb-6"
                >
                    <Link
                        :href="`/mynextcard/card/${card.id}`"
                        class="block w-full rounded-xl overflow-hidden bg-gray-100 dark:bg-slate-700"
                    >
                        <img
                            :src="`${cdnUrl}/${card.card_image?.path}/_small/${card.card_image?.file_name}`"
                            class="w-full h-auto object-contain"
                        />
                    </Link>
                </div>

                <div class="flex flex-col items-stretch gap-2">
                    <div
                        v-if="showHidden && !isEligible"
                        class="flex w-full flex-col items-center gap-3 p-4 rounded-lg bg-slate-100 dark:bg-slate-700/50"
                    >
                        <span
                            class="text-slate-600 dark:text-slate-300 text-sm font-normal leading-5"
                        >
                            You're likely ineligible for this card.
                        </span>
                        <button
                            @click.prevent="openIneligibleModal"
                            class="flex w-full h-9 px-4 justify-center items-center gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            See why
                        </button>
                    </div>

                    <div
                        v-if="
                            isEligible &&
                            card.application_link &&
                            card.issuing_bank.link_relationship_type ===
                                'direct' &&
                            card.issuing_bank.link_button_visible
                        "
                        class="flex flex-col items-center gap-2 w-full"
                    >
                        <a
                            :href="applicationLinkWithMnc"
                            target="_blank"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                        >
                            <span
                                class="text-white text-sm font-medium leading-5"
                            >
                                Apply Now
                            </span>
                            <i class="fa-regular fa-lock text-white"></i>
                        </a>
                        <p class="text-center text-xs text-slate-500 dark:text-slate-400">
                            on {{ card.issuing_bank.name }}'s secure site
                        </p>
                    </div>
                    <a
                        v-else-if="
                            isEligible &&
                            card.application_link &&
                            card.issuing_bank.link_relationship_type ===
                                'indirect' &&
                            card.issuing_bank.link_button_visible
                        "
                        :href="applicationLinkWithMnc"
                        target="_blank"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                    >
                        <span class="text-white text-sm font-medium leading-5">
                            Learn How to Apply
                        </span>
                        <Icon
                            icon="arrow-up-right"
                            class="w-5 h-5 text-white"
                        />
                    </a>
                    <a
                        v-else-if="
                            isEligible &&
                            card.application_link &&
                            card.issuing_bank.link_relationship_type ===
                                'indirect' &&
                            !card.issuing_bank.link_button_visible
                        "
                        :href="applicationLinkWithMnc"
                        target="_blank"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full text-orange hover:text-orange-600 transition-colors duration-200"
                    >
                        <span class="text-sm font-medium leading-5">
                            Learn How to Apply
                        </span>
                        <Icon icon="arrow-up-right" class="w-5 h-5" />
                    </a>
                    <!-- "Read our take" button - shown when there's an orange button above -->
                    <Link
                        v-if="
                            isEligible &&
                            card.application_link &&
                            card.issuing_bank &&
                            card.issuing_bank.link_button_visible
                        "
                        :href="`/mynextcard/card/${card.id}`"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600"
                    >
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Read our take
                        </span>
                    </Link>

                    <!-- "Read our take" link - shown when link only or ineligible -->
                    <Link
                        v-else
                        :href="`/mynextcard/card/${card.id}`"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 w-full text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:text-slate-300 transition-colors"
                    >
                        <span class="text-sm font-medium leading-5">
                            Read our take
                        </span>
                    </Link>
                </div>

                <h3 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7 mt-8">
                    <Link
                        v-if="card"
                        :href="`/mynextcard/card/${card.id}`"
                        class="hover:text-slate-600 dark:hover:text-slate-300"
                    >
                        {{ card.title }}
                    </Link>
                    <span v-else>{{ card.title }}</span>
                </h3>

                <div class="flex items-start gap-2 flex-wrap mt-2">
                    <div
                        v-if="card.type === 'business'"
                        class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                    >
                        <Icon icon="business" class="w-4 h-4 text-slate-800 dark:text-slate-100" />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Business
                        </span>
                    </div>
                    <div
                        v-if="card.program && card.program.type"
                        class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                    >
                        <Icon
                            :icon="getProgramIcon(card.program.type)"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                        />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            {{ goodProgramName(card.program.type) }} Rewards
                        </span>
                    </div>
                    <div
                        v-if="
                            show524 &&
                            (card.type === 'personal' ||
                                (card.type === 'business' &&
                                    card.allow_five_twenty_four_rule))
                        "
                        class="flex py-1 px-3 items-center gap-2 rounded-full border border-orange-200 dark:border-orange-400/40 bg-orange-50 dark:bg-orange-500/15"
                    >
                        <Icon icon="credit-cards" class="w-4 h-4 text-orange" />
                        <span class="text-orange text-sm font-medium leading-5">
                            Card adds to your 5/24
                        </span>
                    </div>
                </div>

                <div
                    v-if="card.rich_text"
                    class="mt-4 pb-4 border-b border-slate-200 dark:border-slate-700"
                >
                    <div class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5">
                        Welcome offer
                    </div>
                    <div class="flex flex-col items-start gap-1 mt-1">
                        <div
                            v-safe-html="card.rich_text"
                            class="welcome-offer-content w-full"
                        ></div>
                    </div>
                </div>

                <div
                    class="mt-4 pb-4 border-b border-slate-200 dark:border-slate-700"
                >
                    <div class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5">
                        Annual fee
                    </div>
                    <div class="flex items-end gap-1 mt-1">
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            ${{ card.annual_fee }}
                        </span>
                        <template
                            v-if="
                                card.issuing_bank &&
                                (card.issuing_bank.name ===
                                    'American Express' ||
                                    card.issuing_bank.name === 'Citi') &&
                                card.rates_and_fees_link
                            "
                        >
                            <a
                                :href="card.rates_and_fees_link"
                                target="_blank"
                                class="ml-1 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                            >
                                (Rates and fees)
                            </a>
                        </template>
                    </div>
                </div>

                <div
                    class="mt-4 pb-4 border-b border-slate-200 dark:border-slate-700"
                >
                    <div class="flex items-center justify-between">
                        <span
                        class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5"
                        >
                            How to earn points
                        </span>
                        <button
                            v-if="earnRateTokens.length"
                            @click.prevent="
                                card.showTipsList = !card.showTipsList
                            "
                            class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                            :aria-expanded="String(!!card.showTipsList)"
                            aria-controls="earn-points-mobile"
                        >
                            <Icon
                                :icon="
                                    card.showTipsList
                                        ? 'chevron-up'
                                        : 'chevron-down'
                                "
                                class="w-4 h-4 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                    </div>
                    <div class="mt-2">
                        <div
                            v-if="!card.showTipsList"
                            :class="[
                                'flex gap-4 flex-wrap',
                                earnRateTokens.length === 1
                                    ? 'items-start'
                                    : 'items-center',
                            ]"
                        >
                            <template v-if="earnRateTokens.length">
                                <div
                                    v-for="rate in earnRateTokens"
                                    :key="rate.multiplier + '-' + rate.category"
                                    class="flex items-center gap-2"
                                >
                                    <div
                                        class="flex justify-center items-center min-w-[3rem] px-2 py-0.5 rounded bg-orange-50"
                                    >
                                        <span
                                            class="text-orange text-sm font-semibold leading-5"
                                        >
                                            {{ rate.multiplier }}
                                        </span>
                                    </div>
                                    <span
                                        class="text-slate-800 dark:text-slate-100 text-sm leading-5"
                                    >
                                        {{ rate.category }}
                                    </span>
                                </div>
                            </template>
                            <template v-else>
                                <span class="text-slate-500 dark:text-slate-400 text-sm leading-5">
                                    No category bonuses available for this card
                                    at the moment.
                                </span>
                            </template>
                        </div>
                        <ul
                            v-else
                            class="flex flex-col gap-4"
                            id="earn-points-mobile"
                        >
                            <li
                                v-for="(tip, idx) in sortedEarnTips"
                                :key="'earn-tip-m-' + idx"
                                :class="[
                                    'flex gap-2',
                                    earnRateTokens.length === 1
                                        ? 'items-center'
                                        : 'items-start',
                                ]"
                            >
                                <div
                                    class="flex justify-center items-center min-w-[3rem] px-2 py-0.5 rounded bg-orange-50"
                                >
                                    <span
                                        class="text-orange text-sm font-semibold leading-5"
                                    >
                                        {{ tip.multiplier }}
                                    </span>
                                </div>
                                <p class="text-slate-800 dark:text-slate-100 text-sm leading-5">
                                    {{ tip.value }}
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>

                <div
                    class="mt-4 pb-4 border-b border-slate-200 dark:border-slate-700"
                >
                    <div class="flex items-center justify-between">
                        <span
                        class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5"
                        >
                            Benefits
                        </span>
                        <button
                            v-if="matchedBenefits.length > 0"
                            @click.prevent="
                                card.showBenefitsList = !card.showBenefitsList
                            "
                            class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded relative z-50"
                        >
                            <Icon
                                :icon="
                                    card.showBenefitsList
                                        ? 'chevron-up'
                                        : 'chevron-down'
                                "
                                class="w-4 h-4 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                    </div>
                    <div class="mt-2">
                        <div v-if="matchedBenefits.length > 0">
                            <div
                                v-if="!card.showBenefitsList"
                                class="flex flex-wrap items-start gap-8 self-stretch"
                            >
                                <Tooltip
                                    v-for="benefit in matchedBenefits"
                                    :key="benefit.id || benefit.benefit_title"
                                    :text="
                                        benefit.benefit_title ||
                                        benefit.benefitTitle
                                    "
                                    xPlacement="center"
                                    yPlacement="top"
                                >
                                    <template #trigger>
                                        <div
                                            class="flex w-9 h-9 justify-center items-center rounded-full bg-orange-50"
                                        >
                                            <benefit-icon svgClass="w-6 h-6 text-orange dark:text-orange-600"
                                                :category="benefit.category"
                                                :title="
                                                    benefit.benefit_title || ''
                                                "
                                                :description="
                                                    benefit.benefit_desc || ''
                                                "
                                                :bgColorClass="'bg-orange-50'"
                                            />
                                        </div>
                                    </template>
                                </Tooltip>
                            </div>
                            <div v-else class="space-y-4">
                                <div
                                    v-for="benefit in matchedBenefits"
                                    :key="benefit.id || benefit.benefit_title"
                                    class="flex items-start gap-3"
                                >
                                    <div
                                        class="flex w-9 h-9 justify-center items-center rounded-full bg-orange-50 shrink-0"
                                    >
                                        <benefit-icon svgClass="w-6 h-6 text-orange dark:text-orange-600"
                                            :category="benefit.category"
                                            :title="benefit.benefit_title || ''"
                                            :description="
                                                benefit.benefit_desc || ''
                                            "
                                            :bgColorClass="'bg-orange-50'"
                                        />
                                    </div>
                                    <div class="flex-1">
                                        <h4
                                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 mb-1"
                                        >
                                            {{
                                                benefit.benefit_title ||
                                                benefit.benefitTitle
                                            }}
                                        </h4>
                                        <p
                                            class="text-slate-600 dark:text-slate-300 text-sm leading-6"
                                        >
                                            {{
                                                benefit.benefit_desc ||
                                                benefit.benefitDesc
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <span class="text-slate-500 dark:text-slate-400 text-sm leading-5">
                                There are currently no benefits available for
                                this card.
                            </span>
                        </div>
                    </div>
                </div>

                <div class="mt-4 pb-4">
                    <div class="flex items-center justify-between">
                        <span
                            class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5"
                        >
                            10xT Thoughts
                        </span>
                        <button
                            v-if="
                                card.thoughts &&
                                card.thoughts.trim() &&
                                isThoughtsLong(card.thoughts)
                            "
                            @click.prevent="
                                card.showThoughts = !card.showThoughts
                            "
                            class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                        >
                            <Icon
                                :icon="
                                    card.showThoughts
                                        ? 'chevron-up'
                                        : 'chevron-down'
                                "
                                class="w-4 h-4 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                    </div>
                    <div class="relative mt-2">
                        <div
                            v-if="card.thoughts && card.thoughts.trim()"
                            :class="[
                                'text-slate-800 dark:text-slate-100 text-sm leading-5 transition-all duration-300',
                                !card.showThoughts
                                    ? 'max-h-16 overflow-hidden'
                                    : '',
                            ]"
                            v-safe-html="card.thoughts"
                        ></div>
                        <div v-else class="text-slate-500 dark:text-slate-400 text-sm leading-5">
                            We're still analyzing this card. Check back soon for
                            our detailed thoughts and recommendations.
                        </div>
                        <div
                            v-if="
                                card.thoughts &&
                                card.thoughts.trim() &&
                                isThoughtsLong(card.thoughts) &&
                                !card.showThoughts
                            "
                            class="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white via-white dark:from-slate-800 dark:via-slate-800 to-transparent pointer-events-none"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="col-span-12 relative hidden md:block" v-if="shouldShowCard">
        <div
            class="absolute left-0 top-[-2.2rem] z-10 flex items-center gap-2 flex-wrap"
        >
            <div
                class="flex items-start gap-2 pt-2 pr-6 pb-4 pl-4 bg-[var(--Primary-10x-Orange,#FD421D)] text-[var(--text-icons-primary-dark-bg,#FFF)] shadow"
                :style="{
                    borderRadius:
                        'var(--radius-md, 8px) var(--radius-md, 8px) 0 0',
                }"
            >
                <Icon icon="sparkles" class="w-4 h-4 shrink-0" />
                <span class="text-sm font-medium leading-5 whitespace-nowrap">
                    {{ user && user.name ? `${user.name}'s` : 'Your' }} top
                    recommended card
                </span>
            </div>
            <SpecialTag
                v-if="card.special_offer"
                :text="'Special welcome offer'"
                variant="secondary-soft-tab"
            />
        </div>
        <div
            class="relative z-20 flex p-6 items-start gap-8 w-full rounded-lg bg-white dark:bg-slate-800 border border-orange overflow-visible shadow-sm"
            :class="{
                'excluded-card': card.hidden,
                'recommended-card': !card.hidden,
            }"
        >
            <CardExclusion
                :card="card"
                :offenders="offenders[card.id]"
                v-if="card.hidden && !hideOverlayOnly && !showHidden"
                @hide-card="hideCard"
            />
            <div class="flex flex-col md:flex-row items-start gap-8 w-full">
                <div
                    class="flex flex-col items-center gap-4 md:w-80 md:min-w-[320px]"
                >
                    <Link
                        :href="`/mynextcard/card/${card.id}`"
                        class="w-80 h-[198px] rounded-xl overflow-hidden bg-gray-100 dark:bg-slate-700 flex items-center justify-center"
                    >
                        <img
                            :src="`${cdnUrl}/${card.card_image?.path}/_small/${card.card_image?.file_name}`"
                            class="w-full h-full object-contain"
                        />
                    </Link>
                    <div
                        class="hidden md:flex flex-col items-start gap-2 w-full mt-2"
                    >
                        <div
                            v-if="showHidden && !isEligible"
                            class="flex w-full flex-col items-center gap-3 p-4 rounded-lg bg-slate-100 dark:bg-slate-700/50"
                        >
                            <span
                                class="text-slate-600 dark:text-slate-300 text-sm font-normal leading-5"
                            >
                                You're likely ineligible for this card.
                            </span>
                            <button
                                @click.prevent="openIneligibleModal"
                                class="flex w-72 h-9 px-4 justify-center items-center gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                See why
                            </button>
                        </div>
                        <div
                            v-if="
                                isEligible &&
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'direct' &&
                                card.issuing_bank.link_button_visible
                            "
                            class="flex flex-col items-center gap-2 w-full"
                        >
                            <a
                                :href="applicationLinkWithMnc"
                                target="_blank"
                                class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                            >
                                <span
                                    class="text-white text-sm font-medium leading-5"
                                >
                                    Apply Now
                                </span>
                                <i class="fa-regular fa-lock text-white"></i>
                            </a>
                            <p class="text-center text-xs text-slate-500 dark:text-slate-400">
                                on {{ card.issuing_bank.name }}'s secure site
                            </p>
                        </div>
                        <a
                            v-else-if="
                                isEligible &&
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'indirect' &&
                                card.issuing_bank.link_button_visible
                            "
                            :href="applicationLinkWithMnc"
                            target="_blank"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-orange shadow-sm hover:bg-orange-600 transition-colors duration-200"
                        >
                            <span
                                class="text-white text-sm font-medium leading-5"
                            >
                                Learn How to Apply
                            </span>
                            <Icon
                                icon="arrow-up-right"
                                class="w-5 h-5 text-white"
                            />
                        </a>
                        <a
                            v-else-if="
                                isEligible &&
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'indirect' &&
                                !card.issuing_bank.link_button_visible
                            "
                            :href="applicationLinkWithMnc"
                            target="_blank"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 w-full text-orange hover:text-orange-600 transition-colors duration-200"
                        >
                            <span class="text-sm font-medium leading-5">
                                Learn How to Apply
                            </span>
                            <Icon icon="arrow-up-right" class="w-5 h-5" />
                        </a>
                        <Link
                            :href="`/mynextcard/card/${card.id}`"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 w-full rounded bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
                        >
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Read our take
                            </span>
                        </Link>
                    </div>
                </div>

                <div class="flex flex-col items-start flex-1">
                    <a
                        v-if="userSettings.is_admin"
                        :href="'/admin/cards/' + card.id + '/edit'"
                        target="blank"
                        class="block absolute top-2 right-2 hover:text-gray-400 dark:hover:text-slate-200"
                    >
                        <i class="fa-regular fa-pen-to-square"></i>
                    </a>

                    <h3
                        class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7 mb-2"
                    >
                        <Link
                            v-if="card"
                            :href="`/mynextcard/card/${card.id}`"
                            class="hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            {{ card.title }}
                        </Link>
                        <span v-else>{{ card.title }}</span>
                    </h3>
                    <div class="flex pb-4 items-start gap-2 flex-wrap">
                        <div
                            v-if="card.type === 'business'"
                            class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                        >
                            <Icon
                                icon="business"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                            />
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Business
                            </span>
                        </div>
                        <div
                            v-if="card.program && card.program.type"
                            class="flex py-1 px-3 items-center gap-2 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                        >
                            <Icon
                                :icon="getProgramIcon(card.program.type)"
                                class="w-4 h-4 text-slate-800 dark:text-slate-100"
                            />
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ goodProgramName(card.program.type) }} Rewards
                            </span>
                        </div>
                        <div
                            v-if="
                                show524 &&
                                (card.type === 'personal' ||
                                    (card.type === 'business' &&
                                        card.allow_five_twenty_four_rule))
                            "
                            class="flex py-1 px-3 items-center gap-2 rounded-full border border-orange-200 dark:border-orange-400/40 bg-orange-50 dark:bg-orange-500/15"
                        >
                            <Icon
                                icon="credit-cards"
                            class="w-4 h-4 text-orange dark:text-orange-300"
                            />
                            <span
                            class="text-orange dark:text-orange-300 text-sm font-medium leading-5"
                            >
                                Card adds to your 5/24
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="card.rich_text"
                        class="flex items-start pb-4 gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                    >
                        <span
                            class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 min-w-[160px]"
                        >
                            Welcome offer
                        </span>
                        <div class="flex-1 flex flex-col items-start gap-1">
                            <div
                                v-safe-html="card.rich_text"
                                class="welcome-offer-content w-full"
                            ></div>
                        </div>
                    </div>
                    <div
                        class="flex py-[18px] items-center gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                    >
                        <span
                            class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 min-w-[160px]"
                        >
                            Annual fee
                        </span>
                        <div class="flex items-end gap-1 flex-1">
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                            >
                                ${{ card.annual_fee }}
                            </span>
                            <template
                                v-if="
                                    card.issuing_bank &&
                                    (card.issuing_bank.name ===
                                        'American Express' ||
                                        card.issuing_bank.name === 'Citi') &&
                                    card.rates_and_fees_link
                                "
                            >
                                <a
                                    :href="card.rates_and_fees_link"
                                    target="_blank"
                                    class="ml-1 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                >
                                    (Rates and fees)
                                </a>
                            </template>
                        </div>
                    </div>

                    <div
                        class="flex py-4 items-center gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                    >
                        <span
                            class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 min-w-[160px]"
                        >
                            How to earn points
                        </span>
                        <div class="flex-1">
                            <div
                                v-if="!card.showTipsList"
                                :class="[
                                    'flex gap-4 flex-wrap',
                                    earnRateTokens.length === 1
                                        ? 'items-start'
                                        : 'items-center',
                                ]"
                            >
                                <template v-if="earnRateTokens.length">
                                    <div
                                        v-for="rate in earnRateTokens"
                                        :key="
                                            rate.multiplier +
                                            '-' +
                                            rate.category
                                        "
                                        class="flex items-center gap-2"
                                    >
                                        <div
                                            class="flex justify-center items-center min-w-[3rem] px-2 py-0.5 rounded bg-orange-50"
                                        >
                                            <span
                                                class="text-orange text-sm font-semibold leading-5"
                                            >
                                                {{ rate.multiplier }}
                                            </span>
                                        </div>
                                        <span
                                    class="text-slate-800 dark:text-slate-100 text-sm leading-5"
                                        >
                                            {{ rate.category }}
                                        </span>
                                    </div>
                                </template>
                                <template v-else>
                                    <span
                                        class="text-slate-500 dark:text-slate-400 text-sm leading-5"
                                    >
                                        No category bonuses available for this
                                        card at the moment.
                                    </span>
                                </template>
                            </div>
                            <ul
                                v-else
                                class="flex flex-col gap-3"
                                id="earn-points"
                            >
                                <li
                                    v-for="(tip, idx) in sortedEarnTips"
                                    :key="'earn-tip-' + idx"
                                    :class="[
                                        'flex gap-2',
                                        earnRateTokens.length === 1
                                            ? 'items-center'
                                            : 'items-start',
                                    ]"
                                >
                                    <div
                                        class="flex justify-center items-center min-w-[3rem] px-2 py-0.5 rounded bg-orange-50"
                                    >
                                        <span
                                            class="text-orange text-sm font-semibold leading-5"
                                        >
                                            {{ tip.multiplier }}
                                        </span>
                                    </div>
                                    <p class="text-slate-800 dark:text-slate-100 text-sm leading-5">
                                        {{ tip.value }}
                                    </p>
                                </li>
                            </ul>
                        </div>
                        <button
                            v-if="earnRateTokens.length"
                            @click.prevent="
                                card.showTipsList = !card.showTipsList
                            "
                            class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded ml-auto"
                        >
                            <Icon
                                :icon="
                                    card.showTipsList
                                        ? 'chevron-up'
                                        : 'chevron-down'
                                "
                                class="w-4 h-4 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                    </div>

                    <div
                        class="flex py-4 items-start gap-6 w-full border-b border-slate-100 dark:border-slate-700"
                    >
                        <span
                            class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 min-w-[160px]"
                        >
                            Benefits
                        </span>
                        <div v-if="matchedBenefits.length > 0" class="flex-1">
                            <div
                                v-if="!card.showBenefitsList"
                                class="flex flex-wrap items-start gap-6"
                            >
                                <Tooltip
                                    v-for="benefit in matchedBenefits"
                                    :key="benefit.id || benefit.benefit_title"
                                    :text="
                                        benefit.benefit_title ||
                                        benefit.benefitTitle
                                    "
                                    xPlacement="center"
                                    yPlacement="top"
                                >
                                    <template #trigger>
                                        <div
                                            class="flex w-9 h-9 justify-center items-center gap-2.5 rounded-full bg-orange-50"
                                        >
                                            <benefit-icon svgClass="w-6 h-6 text-orange dark:text-orange-600"
                                                :category="benefit.category"
                                                :title="
                                                    benefit.benefit_title || ''
                                                "
                                                :description="
                                                    benefit.benefit_desc || ''
                                                "
                                                :bgColorClass="'bg-orange-50'"
                                            />
                                        </div>
                                    </template>
                                </Tooltip>
                            </div>
                            <div v-else class="space-y-4">
                                <div
                                    v-for="benefit in matchedBenefits"
                                    :key="benefit.id || benefit.benefit_title"
                                    class="flex items-start gap-3"
                                >
                                    <div
                                        class="flex w-9 h-9 justify-center items-center gap-2.5 rounded-full bg-orange-50 shrink-0"
                                    >
                                        <benefit-icon svgClass="w-6 h-6 text-orange dark:text-orange-600"
                                            :category="benefit.category"
                                            :title="benefit.benefit_title || ''"
                                            :description="
                                                benefit.benefit_desc || ''
                                            "
                                            :bgColorClass="'bg-orange-50'"
                                        />
                                    </div>
                                    <div class="flex-1">
                                        <h4
                                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 mb-1"
                                        >
                                            {{
                                                benefit.benefit_title ||
                                                benefit.benefitTitle
                                            }}
                                        </h4>
                                        <p
                                            class="text-slate-600 dark:text-slate-300 text-sm leading-6"
                                        >
                                            {{
                                                benefit.benefit_desc ||
                                                benefit.benefitDesc
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="flex items-start gap-4 flex-1">
                            <span class="text-slate-500 dark:text-slate-400 text-sm leading-5">
                                There are currently no benefits available for
                                this card.
                            </span>
                        </div>
                        <button
                            v-if="matchedBenefits.length > 0"
                            @click.prevent="
                                card.showBenefitsList = !card.showBenefitsList
                            "
                            class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded ml-auto relative z-50"
                        >
                            <Icon
                                :icon="
                                    card.showBenefitsList
                                        ? 'chevron-up'
                                        : 'chevron-down'
                                "
                                class="w-4 h-4 text-slate-600 dark:text-slate-300"
                            />
                        </button>
                    </div>

                    <div class="flex py-4 items-start gap-6 w-full">
                        <span
                            class="text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 min-w-[160px]"
                        >
                            10xT Thoughts
                        </span>
                        <div
                            class="flex flex-col items-start gap-1 flex-1 relative"
                        >
                            <div
                                v-if="card.thoughts && card.thoughts.trim()"
                                :class="[
                                    'text-slate-800 dark:text-slate-100 text-sm leading-5 transition-all duration-300',
                                    !card.showThoughts
                                        ? 'max-h-10 overflow-hidden'
                                        : '',
                                ]"
                                v-safe-html="card.thoughts"
                            ></div>
                            <div
                                v-else
                                class="text-slate-500 dark:text-slate-400 text-sm leading-5"
                            >
                                We're still analyzing this card. Check back soon
                                for our detailed thoughts and recommendations.
                            </div>
                            <div
                                v-if="
                                    card.thoughts &&
                                    card.thoughts.trim() &&
                                    isThoughtsLong(card.thoughts) &&
                                    !card.showThoughts
                                "
                                class="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white via-white dark:from-slate-800 dark:via-slate-800 to-transparent pointer-events-none"
                            ></div>
                        </div>
                        <div
                            class="shrink-0"
                            v-if="
                                card.thoughts &&
                                card.thoughts.trim() &&
                                isThoughtsLong(card.thoughts)
                            "
                        >
                            <button
                                @click.prevent="
                                    card.showThoughts = !card.showThoughts
                                "
                                class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                            >
                                <Icon
                                    :icon="
                                        card.showThoughts
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    "
                                    class="w-4 h-4 text-slate-600 dark:text-slate-300"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <IneligibilityModal
            :is-open="showIneligibilityModal"
            :card="card"
            :offenders="
                offenders && offenders[card.id] ? offenders[card.id] : {}
            "
            @close="showIneligibilityModal = false"
        />
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useAppStore } from '@/store/app';
import { useNextCardStore } from '@/store/nextcard';
import { Link } from '@inertiajs/vue3';

import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import CardExclusion from '@/components/next-card/card-exclusion.vue';
import SpecialTag from '@/components/next-card/special-tag.vue';
import CouldContributeToFiveTwentyFour from '@/components/next-card/contribute524Count.vue';
import NextCardTips from '@/components/next-card/next-card-tips.vue';
import Icon from '@/components/ui/icon/icon.vue';
import IneligibilityModal from '@/components/next-card/ineligibility-modal.vue';
import Tooltip from '@/components/tooltip.vue';
import { appendMncToUrl } from '@/utils/urlHelpers.js';

export default {
    name: 'TopCard',
    props: {
        card: {
            type: Object,
        },
        user: {
            type: Object,
            default: null,
        },
    },
    components: {
        BenefitIcon,
        CardExclusion,
        SpecialTag,
        CouldContributeToFiveTwentyFour,
        NextCardTips,
        Icon,
        IneligibilityModal,
        Link,
        Tooltip,
    },
    data() {
        return {
            hideOverlayOnly: false,
            acknowledgedThisSession: false,
            showIneligibilityModal: false,
        };
    },
    methods: {
        ...mapActions(useNextCardStore, ['goodProgramName']),
        getProgramIcon(type) {
            switch (type) {
                case 'flexible_currency':
                case 'travel':
                    return 'airplane';
                case 'airline':
                    return 'airplane';
                case 'hotel':
                    return 'hotel';
                case 'rail':
                    return 'train';
                case 'cash_back':
                    return 'dollar-coin';
                default:
                    return 'credit-cards';
            }
        },
        hideCard() {
            this.hideOverlayOnly = true;
            this.acknowledgedThisSession = true;
        },
        openIneligibleModal() {
            this.showIneligibilityModal = true;
        },
        isThoughtsLong(content) {
            if (!content) return false;
            const txt =
                typeof content === 'string'
                    ? content.replace(/<[^>]*>/g, '')
                    : String(content);
            return txt.trim().length > 150;
        },
        deriveCategoryLabel(text) {
            if (!text) return '';
            let t = String(text)
                .replace(/<[^>]*>/g, '')
                .trim();
            const earnIdx = t.toLowerCase().indexOf(' on ');
            if (earnIdx !== -1) {
                t = t.slice(earnIdx + 4).trim();
            }
            t = t.replace(/\.$/, '').trim();
            t = t.replace(/\s{2,}/g, ' ');
            if (t.length > 60) {
                t = t.slice(0, 57).trim() + '...';
            }
            return t.charAt(0).toUpperCase() + t.slice(1);
        },
        deriveShortCategory(text) {
            if (!text) return '';
            const lower = String(text).toLowerCase();

            if (lower.includes('ihg hotel')) return 'IHG Hotels';

            if (lower.includes('restaurant')) return 'Restaurants';
            if (lower.includes('supermarket') || lower.includes('grocery'))
                return 'Groceries';
            if (lower.includes('flight') || lower.includes('airline'))
                return 'Flights';
            if (lower.includes('hotel')) return 'Hotels';
            if (lower.includes('gas station')) return 'Gas Stations';
            if (lower.includes('gas')) return 'Gas';
            if (
                lower.includes('transit') ||
                lower.includes('subway') ||
                lower.includes('bus') ||
                lower.includes('train')
            )
                return 'Transit';
            if (
                lower.includes('rideshare') ||
                lower.includes('uber') ||
                lower.includes('lyft')
            )
                return 'Rideshare';
            if (lower.includes('stream')) return 'Streaming';
            if (lower.includes('travel')) return 'Travel';
            if (lower.includes('drugstore') || lower.includes('pharm'))
                return 'Drugstores';
            if (lower.includes('office') && lower.includes('suppl'))
                return 'Office supplies';
            if (lower.includes('shipping')) return 'Shipping';
            if (
                lower.includes('internet') ||
                lower.includes('cable') ||
                lower.includes('phone') ||
                lower.includes('utility')
            )
                return 'Utilities';

            if (
                lower.includes('travel') &&
                lower.includes('gas') &&
                lower.includes('restaurant')
            ) {
                return 'Multiple categories';
            }

            if (lower.includes('all other')) return 'All other purchases';

            return this.deriveCategoryLabel(text);
        },
        normalizeMultiplier(text) {
            if (!text) return '';
            const str = String(text).trim();
            const m = str.match(/([0-9]+(?:\.[0-9]+)?)\s*[xX]/);
            if (m) {
                return `${m[1]}x`;
            }
            const numMatch = str.match(/^([0-9]+(?:\.[0-9]+)?)$/);
            if (numMatch) {
                return `${numMatch[1]}x`;
            }
            return str.toLowerCase();
        },
        extractMultiplierValue(multiplier) {
            const m = String(multiplier).match(/([0-9]+(?:\.[0-9]+)?)/);
            return m ? parseFloat(m[1]) : 0;
        },
        processRewardsData(data) {
            if (!data) return { spend_bonus_categories: [], benefits: [] };
            const processed = { ...data };

            // Process benefits_config if available
            if (data.benefits_config) {
                const benefitsConfig = Array.isArray(data.benefits_config)
                    ? data.benefits_config
                    : typeof data.benefits_config === 'string'
                      ? JSON.parse(data.benefits_config || '[]')
                      : [];

                // Handle both string and array formats for benefits
                const benefits = Array.isArray(data.benefits)
                    ? data.benefits
                    : typeof data.benefits === 'string'
                      ? JSON.parse(data.benefits || '[]')
                      : [];

                // If we have benefits and config data, apply ordering and filtering
                if (benefits.length > 0 && benefitsConfig.length > 0) {
                    // Create a map of benefits by title for quick lookup
                    const benefitsMap = new Map();
                    benefits.forEach((benefit) => {
                        const key =
                            benefit.benefit_title || benefit.benefitTitle || '';
                        if (key) {
                            benefitsMap.set(key, benefit);
                        }
                    });

                    // Apply ordering and filtering from config
                    const orderedBenefits = benefitsConfig
                        .filter((config) => !config.hidden) // Filter out hidden benefits
                        .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order
                        .map((config) => {
                            const benefitTitle =
                                config.benefitTitle || config.benefit_title;
                            return benefitsMap.get(benefitTitle);
                        })
                        .filter((benefit) => !!benefit); // Remove any undefined benefits

                    // Replace the benefits array with ordered and filtered benefits
                    processed.benefits = orderedBenefits;
                }
            }

            return processed;
        },
    },
    computed: {
        ...mapState(useAppStore, ['userSettings']),
        ...mapState(useNextCardStore, [
            'filteredNextCards',
            'offenders',
            'showHidden',
            'show524',
        ]),
        cdnUrl() {
            return window.cdnUrl;
        },
        applicationLinkWithMnc() {
            return appendMncToUrl(this.card?.application_link);
        },
        isEligible() {
            return !this.card.hidden;
        },
        shouldShowCard() {
            return true;
        },
        parsedPointsTips() {
            const earnRates = this.card && this.card.earn_rates;
            if (!earnRates) return [];
            if (Array.isArray(earnRates)) return earnRates;
            try {
                const parsed = JSON.parse(earnRates);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        },
        earnRateTokens() {
            const items = this.parsedPointsTips
                .filter(
                    (t) =>
                        t &&
                        (t.label ||
                            t.value ||
                            t.earnMultiplier ||
                            t.spendBonusDesc)
                )
                .map((t) => {
                    const rawMultiplier = t.earnMultiplier || t.label || '';

                    let category = '';
                    if (t.spendBonusCategoryName) {
                        category = t.spendBonusCategoryName;
                    } else if (t.spendBonusDesc || t.value) {
                        const textToDerive = t.spendBonusDesc || t.value || '';
                        category = this.deriveShortCategory(
                            textToDerive.toString()
                        );
                    }

                    const multiplier = this.normalizeMultiplier(
                        rawMultiplier.toString()
                    );
                    const value = this.extractMultiplierValue(multiplier);
                    return { multiplier, category, value };
                })
                .filter((r) => r.multiplier || r.category);
            return items.sort((a, b) => b.value - a.value);
        },
        sortedEarnTips() {
            const items = (this.parsedPointsTips || []).map((t) => {
                const rawMultiplier = t.earnMultiplier || t.label || '';
                const rawValue = t.spendBonusDesc || t.value || '';

                let categoryTitle = '';
                if (t.spendBonusCategoryName) {
                    categoryTitle = t.spendBonusCategoryName;
                } else if (rawValue && !t.spendBonusCategoryName) {
                    categoryTitle = this.deriveShortCategory(rawValue);
                }

                const m = this.normalizeMultiplier(rawMultiplier.toString());
                const v = this.extractMultiplierValue(m);
                return {
                    multiplier: m,
                    value: rawValue,
                    num: v,
                    title: categoryTitle,
                };
            });
            return items.sort((a, b) => b.num - a.num);
        },
        matchedBenefits() {
            const rd = this.card.rewardscc_data || {};
            const processedData = this.processRewardsData(rd);
            const benefits = Array.isArray(processedData.benefits)
                ? processedData.benefits
                : [];
            const benefitsConfig = Array.isArray(rd.benefits_config)
                ? typeof rd.benefits_config === 'string'
                    ? JSON.parse(rd.benefits_config || '[]')
                    : rd.benefits_config
                : [];

            if (!benefits.length) return [];

            // If we have benefits_config, the benefits are already ordered by processRewardsData
            if (benefitsConfig.length > 0) {
                const normalize = (s) =>
                    (s || '').toString().toLowerCase().replace(/\s+/g, '');
                const configMap = {};
                benefitsConfig.forEach((cfg) => {
                    const key = normalize(
                        cfg.benefit_title || cfg.benefitTitle || ''
                    );
                    if (key) configMap[key] = cfg;
                });

                // Just add icon and other metadata, preserve the order from processRewardsData
                return benefits.map((b) => {
                    const titleRaw =
                        b.benefit_title ?? b.benefitTitle ?? b.title ?? '';
                    const descRaw =
                        b.benefit_desc ?? b.benefitDesc ?? b.desc ?? '';
                    const cfg = configMap[normalize(titleRaw)];
                    const result = {
                        ...b,
                        benefit_title: titleRaw,
                        benefit_desc: descRaw,
                        icon: cfg?.icon ?? 'card',
                        order: cfg?.order ?? 999,
                        hidden: cfg?.hidden ?? false,
                    };
                    return result;
                });
            }

            // Fallback for cards without benefits_config
            return benefits.map((b) => {
                const titleRaw =
                    b.benefit_title ?? b.benefitTitle ?? b.title ?? '';
                const descRaw = b.benefit_desc ?? b.benefitDesc ?? b.desc ?? '';
                return {
                    ...b,
                    benefit_title: titleRaw,
                    benefit_desc: descRaw,
                    icon: 'card',
                    order: 999,
                    hidden: false,
                };
            });
        },
    },
    created() {},
    mounted() {},
};
</script>
<style lang="scss" scoped>
.recommended-card {
    opacity: 1;
}

/* 10xT Thoughts paragraph spacing */
:deep(.text-slate-800 dark:text-slate-100 p) {
    margin-bottom: 1rem;
}

:deep(.text-slate-800 dark:text-slate-100 p:last-child) {
    margin-bottom: 0;
}

/* Welcome Offer Styling with Tailwind classes */
:deep(.welcome-offer-content) {
    .current-offer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        margin-bottom: 0;
    }

    h4 {
        flex: 1 0 0;
        color: #1e293b;
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        margin-bottom: 0;

        span.text-orange {
            color: #fd421d;
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
        }
    }

    p {
        align-self: stretch;
        color: #1e293b;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        margin-bottom: 0;
    }
}
</style>

<style>
/* Dark-mode override for v-html welcome-offer content (no scope id on injected nodes) */
.dark .welcome-offer-content h4,
.dark .welcome-offer-content p {
    color: #f1f5f9;
}
</style>
